/**
 * Which hero `/` and `/men` render. Shared so both homepages switch together.
 *  - "vwoTest": BLACK FRIDAY server-side VWO hero tests, one per homepage: `/` on
 *    HOMEPAGE_HERO_FLAG (NT2-32946), `/men` on MEN_HOMEPAGE_HERO_FLAG (NT2-33410).
 *    default (control) = pill hero + Cf promo, variation1 = BFCM splash (heroA).
 *  - "regular": BAU hero, no VWO. `/` = Strand Serum bundle hero (NT2-33283), `/men` = pill-circle
 *    hero. QuizResurfaceBanner overlays the hero only in this mode (bundle art sits under it).
 */
export type HomepageHeroMode = "vwoTest" | "regular";

export const HOMEPAGE_HERO_MODE: HomepageHeroMode = "vwoTest";

/**
 * VWO's own visitor cookie, the id for FME bucketing when there is no `_vwo_uuid_v2`. SmartCode
 * creates it in the browser; on a first visit middleware creates it first (SmartCode then adopts
 * it), so server-side FME and SmartCode see the same visitor. Must stay readable by JS (not
 * HttpOnly) or SmartCode can't see it and would mint a different id.
 */
export const VWO_SMARTCODE_ID_COOKIE = "_vwo_uuid";

/**
 * SmartCode's cross-subdomain visitor cookie on `.nutrafol.com`, "<id>|<hash>". SmartCode records
 * sessions and goals under this id, so when it holds one it wins over `_vwo_uuid`: otherwise a
 * visitor who already has it (e.g. from www) gets impressions and conversions on different ids.
 */
export const VWO_SMARTCODE_ID_V2_COOKIE = "_vwo_uuid_v2";

export const VWO_VISITOR_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 365;
/* Homepage SSR must never wait long on VWO; every timeout falls back to the default value. */
export const VWO_INIT_TIMEOUT_MS = 1000;
export const VWO_FLAG_TIMEOUT_MS = 300;
export const VWO_FLUSH_TIMEOUT_MS = 3000;

/** After a failed SDK init, wait this long before trying again (avoids hammering VWO). */
export const VWO_INIT_RETRY_COOLDOWN_MS = 30_000;

/**
 * Max age of a stored FME settings record before the SDK ignores it and fetches live. Long on
 * purpose, so a loaded record stays usable for the life of the process.
 *
 * This is NOT how fast dashboard changes arrive. The SDK polls every `pollInterval` (10 min,
 * see vwoClient.ts) and refreshes settings in the background even when the stored record is
 * still valid, so a running instance picks up VWO changes within roughly two poll cycles.
 */
export const VWO_SETTINGS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * How often a running instance re-checks VWO for changed settings. Effectively "only on
 * redeploy / instance restart". Must stay below 2^31 - 1 ms (about 24.8 days): Node clamps a
 * longer timer to 1 ms, which would make the SDK poll VWO in a tight loop. Because a VWO change
 * (including pausing a bad variation) will not reach a running instance until it restarts,
 * redeploy to apply urgent changes.
 */
export const VWO_POLL_INTERVAL_MS = 24 * 24 * 60 * 60 * 1000;

/* ---------------------------------------------------------------- FME Feature Keys in use */

/**
 * Homepage hero tests (Black Friday). Separate flags on purpose so men and masterbrand results are
 * read apart; a visitor is bucketed independently on each homepage and may see different arms.
 */
/* `/` (HomepageHeroWrapper), NT2-32946. */
export const HOMEPAGE_HERO_FLAG = "bfMasterbrandHomepageHeroNt232946";
/* `/men` (HomepageHeroWrapperMen), NT2-33410. */
export const MEN_HOMEPAGE_HERO_FLAG = "bfMensHomepageHeroNt233410";
